// React
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

// Hooks
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";
import { useQueryParams } from "@/Common/hooks/Query/useQueryParams";

// API
import { onCreateSalesInvoices } from "@DataPages/api/chargebacks";

// Components
import { useLocationId } from "@/Common/hooks/Location/useLocationId";
import BodyLayer from "@/Layouts/components/Layers/BodyLayer";
import CommissionSideBar from "@/Layouts/components/SideBars/CommissionSideBar";
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import ChargebacksModel from "./ChargebacksModel";

const QUERY_IDENTIFIERS = ["chargebacks"];

export default function ChargebacksLayer(props) {
	const [, setSearchParams] = useSearchParams();
	const { clearCache } = useCacheCleaner();
	const lastSelected = useRef(null);
	const [selectedCommissionCycle, setSelectedCommissionCycle] = useState(null);
	const [currentCommissionCycle, setCurrentCommissionCycle] = useState(null);
	const { addSubscription, clearSubscriptionContext } = useSubscriptions();
	const locationId = useLocationId();
	const { location: queryLocation, commissionCycle: queryCommissionCycle } = useQueryParams();

	const effectiveLocationId = queryLocation?.locationId || locationId;

	const sideBar = useMemo(() => {
		const handleSelection = function (selected, isActualCycle) {
			const commissionCycleId = selected?._id;
			if (commissionCycleId !== lastSelected?._id) {
				lastSelected.current = selected;
				setSelectedCommissionCycle(selected);

				if (effectiveLocationId && commissionCycleId) {
					addSubscription(
						["salesInvoices", effectiveLocationId, commissionCycleId],
						[
							{
								context: "onCreateSalesInvoices",
								query: onCreateSalesInvoices,
								variables: { locationId: effectiveLocationId, commissionCycleId },
							},
						]
					);
				}

				if (queryCommissionCycle && commissionCycleId !== queryCommissionCycle._id) {
					setSearchParams({});
				}

				setCurrentCommissionCycle(isActualCycle);
			}
		};

		return (
			<CommissionSideBar
				selected={queryCommissionCycle}
				callbackSelected={handleSelection}
				selectDefaultLastCommissionCycle={!queryCommissionCycle}
			/>
		);
	}, [addSubscription, effectiveLocationId, queryCommissionCycle]);

	useEffect(() => {
		if (!queryCommissionCycle || selectedCommissionCycle) {
			return;
		}
		lastSelected.current = queryCommissionCycle;
		setSelectedCommissionCycle(queryCommissionCycle);
		setCurrentCommissionCycle(false);

		if (effectiveLocationId && queryCommissionCycle?._id) {
			addSubscription(
				["salesInvoices", effectiveLocationId, queryCommissionCycle._id],
				[
					{
						context: "onCreateSalesInvoices",
						query: onCreateSalesInvoices,
						variables: { locationId: effectiveLocationId, commissionCycleId: queryCommissionCycle._id },
					},
				]
			);
		}
	}, [addSubscription, effectiveLocationId, queryCommissionCycle, selectedCommissionCycle]);

	useEffect(() => {
		return () => {
			lastSelected.current = null;
			setSelectedCommissionCycle(null);
			clearCache(QUERY_IDENTIFIERS);
			clearSubscriptionContext();
		};
	}, [props, locationId, clearSubscriptionContext]);

	return (
		<BodyLayer fixedContent={sideBar}>
			<RoundedBox sx={{ minHeight: "630px" }} className="!pb-[48px]">
				<ChargebacksModel
					commissionCycle={selectedCommissionCycle}
					locationId={effectiveLocationId}
					showUpload={!!currentCommissionCycle}
				/>
			</RoundedBox>
		</BodyLayer>
	);
}
